import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import {
  isPlatformDomain,
  PLATFORM_PRODUCTION_DOMAIN,
  PLATFORM_STAGING_DOMAIN,
} from 'frontend-utils/utils/domain-checks';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import { COMPONENT_DOMAINS } from 'lumen/utils/constants/general-constants';

interface HotelRatesTablesCellsThHotelSignature {
  Args: {
    column: {
      componentDomain?: string;
      hasMargin: boolean;
      hotelinfoId?: string;
      infoText?: string;
      infoTextTitle?: string;
      isDisconnected: boolean;
      isParityOtaOutOfSync: boolean;
      name: string;
      otaId: string;
      textAlign?: string;
    };
  };
}

export default class HotelRatesTablesCellsThHotel extends Component<HotelRatesTablesCellsThHotelSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  get hasNoIcon(): boolean {
    return (
      !this.args.column.infoText &&
      !this.args.column.isDisconnected &&
      !this.args.column.isParityOtaOutOfSync
    );
  }

  get hasRedirectToUserManagement(): boolean {
    return (
      this.access.isStaffUser && this.args.column.componentDomain !== undefined
    );
  }

  get showMargin(): boolean {
    return this.hasNoIcon && this.args.column.hasMargin !== false;
  }

  get textAlign(): string {
    const align = this.args.column.textAlign;

    if (align === 'left') {
      return 'justify-start';
    }
    if (align === 'center') {
      return 'justify-center';
    }
    return 'justify-end';
  }

  @action
  redirectToUserManagement(): void {
    if (this.access.isStaffUser) {
      if (this.args.column.componentDomain === COMPONENT_DOMAINS.RATES) {
        if (this.args.column.hotelinfoId) {
          // Admin only feature, so no further domain checks necessary here
          const url = isPlatformDomain()
            ? `https://${window.location.hostname}/admin/hotel_industry/hotelinfo/${this.args.column.hotelinfoId}/`
            : `https://${PLATFORM_PRODUCTION_DOMAIN}/admin/hotel_industry/hotelinfo/${this.args.column.hotelinfoId}/`;

          openUrl(url);
        }
      }

      // Parity list is also used in the market PI historical route. In that case we don't have the correct data for these redirects
      // Use this flag to detect this scenario
      const isParityInsightHistoricTable =
        window.location.href.indexOf('market') !== -1;

      if (this.args.column.componentDomain === COMPONENT_DOMAINS.PARITY) {
        if (!isParityInsightHistoricTable) {
          // Admin only feature, so no further domain checks necessary here
          const url = isPlatformDomain()
            ? `https://${window.location.hostname}/redirect/matching-admin/${this.hotelAndMarketService.hotel?.id}/${this.args.column.otaId}/`
            : `https://${PLATFORM_STAGING_DOMAIN}/redirect/matching-admin/${this.hotelAndMarketService.hotel?.id}/${this.args.column.otaId}/`;

          openUrl(url);
        }
      }

      // Print a link for each metasearch channel to the UM matching
      if (this.args.column.componentDomain === COMPONENT_DOMAINS.PARITY_META) {
        if (!isParityInsightHistoricTable) {
          const strConsoleLayout = 'color: #005EA0; font-weight: bold;';
          console.group('%cMetasearch channels', strConsoleLayout);
          this.hotelAndMarketService.hotel?.OTAsParity.forEach((ota) => {
            if (!ota.isMajor) {
              // Admin only feature, so no further domain checks necessary here
              const linkToMetaChannel = isPlatformDomain()
                ? `https://${window.location.hostname}/redirect/matching-admin/${this.hotelAndMarketService.hotel?.id}/${ota.id}/`
                : `https://${PLATFORM_STAGING_DOMAIN}/redirect/matching-admin/${this.hotelAndMarketService.hotel?.id}/${ota.id}/`;

              console.log(
                `%c${ota.name}: ${linkToMetaChannel}`,
                strConsoleLayout,
              );
            }
          });
          console.groupEnd();
        }
      }
    }
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div
      class="rates-table-header-cell text-200--bold flex gap-100
        {{if this.hasRedirectToUserManagement 'is-link'}}
        {{this.textAlign}}"
      role="button"
      title={{@column.name}}
      {{on "click" this.redirectToUserManagement}}
    >
      <div class="line-clamp-2 {{if this.showMargin 'mr-500'}}">
        {{@column.name}}
      </div>

      {{#unless this.hasNoIcon}}
        <div class="rates-table-header-cell--icon flex items-center">
          {{#if @column.infoText}}
            <GeneralIconWithTooltip
              @side="bottom"
              @size="small"
              @tooltipContent={{@column.infoText}}
              @tooltipTitle={{@column.infoTextTitle}}
            />
          {{/if}}

          {{#if @column.isDisconnected}}
            <GeneralIconWithTooltip
              @iconName="connection"
              @side="top"
              @size="small"
              @tooltipContent={{t "rates.disconnected_ota"}}
            />
          {{else if @column.isParityOtaOutOfSync}}
            <GeneralIconWithTooltip
              @iconName="sync-disabled"
              @side="top"
              @size="small"
              @tooltipContent={{t "rates.out_of_sync_message"}}
            />
          {{/if}}
        </div>
      {{/unless}}
    </div>
  </template>
}
